import { ThemeColorsT } from "get-life-storybook-ts/lib/constants/GetlifeStyles";
import { UserStatusT } from "../../../context/GetLifeContext.context";
import HttpGlobal from "../Global/HttpGlobal.class";
import {
  calculatorOnGateway,
  clearNativeMode,
  customerApiBaseUrl,
  enterNativeMode,
  isNativeMode,
  nativeGatewayBaseUrl,
  organicNativeEnabled,
  readOrganicToken,
  storeOrganicToken,
} from "../../../native/nativeMode";
import { readOrganicOffer, storeOrganicOffer } from "../../../native/organicOffer";
import { facileBirthDate, facileGender, facilePricesFromRate } from "../../../native/facileNative";
import {
  BeneficiaryDesignationT,
  BeneficiaryDesignationViewI,
} from "../../../views/Questions/PostPayment/components/BeneficiaryClause/beneficiaryClause";
import {
  CapitalsResponse,
  DataI,
  DataPublicI,
  FacileBasePriceI,
  FacileLeadI,
  PostFacileLeadI,
  PriceDashboardI,
} from "./Interfaces/DataInterface.response";
import {
  InsuranceCalculateDataI,
  InsuranceCalculateI,
} from "./Interfaces/InsuranceCalculate";
import {
  OrganicRequestI,
  OrganicResponseI,
} from "./Interfaces/OrganicInterface";
import { ProductI } from "./Interfaces/ProductInterface.response";
import { GetLeadDocumentationI } from "./Interfaces/GetLeadDocumentation";
import { BeneficiaryI } from "./Interfaces/BeneficiaryInterface";
import {
  PersonProjectI,
  SwitchLeadResponseI,
  ArchiveQuoteResponseI,
} from "./Interfaces/PersonProjectInterface";
import {
  CapitalChangePreviewI,
  CapitalChangeResultI,
} from "./Interfaces/CapitalChangeInterface";
import { PolicyI } from "./Interfaces/PolicyInterface";
import { PricingI } from "./Interfaces/PricingInterface";
import { DocumentsI } from "./Interfaces/DocumentsInterface";

/** El enlace de login del gateway es un JWT (`a.b.c`); el OTP del monolito no. */
export const isGatewayLoginLink = (otp: unknown): boolean =>
  "string" === typeof otp && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(otp);

export default class HttpLeadRepository extends HttpGlobal {
  private readonly API_URL:
    | string
    | undefined = `${process.env.REACT_APP_CUSTOMER_APP_URL}api`;

  private readonly API_URL_LEAD:
    | string
    | undefined = `${process.env.REACT_APP_CUSTOMER_APP_URL}api/lead`;

  private readonly API_URL_LEAD_CHECKOUT:
    | string
    | undefined = `${this.API_URL_LEAD}/checkout`;

  private readonly API_URL_LEAD_BENEFICIARIES:
    | string
    | undefined = `${this.API_URL_LEAD_CHECKOUT}/beneficiaries`;

  private readonly API_URL_DOCUMENT:
    | string
    | undefined = `${process.env.REACT_APP_CUSTOMER_APP_URL}api/documents`;

  private readonly API_URL_EVENTS:
    | string
    | undefined = `${process.env.REACT_APP_EVENTS_API}public/front/event`;

  getBeneficiaries = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD_BENEFICIARIES}/${this.leadId}`
    );
    return response;
  };

  getBeneficiariesTypes = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD_BENEFICIARIES}/types/${this.leadId}`
    );
    return response;
  };

  getCheckoutData = async (): Promise<DataI> => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/checkout-data`
      : `${this.API_URL_LEAD_CHECKOUT}/data/get/${this.leadId}`;
    const response: DataI = await this.generalGet(url);
    return response;
  };

  getPriceDashboard = async (): Promise<PriceDashboardI> => {
    const response: any = await this.generalGet(
      `${this.API_URL}/dashboard/next-price/${this.leadId}`
    );
    return response;
  };

  getCheckoutProduct = async (): Promise<ProductI> => {
    const response: ProductI = await this.generalGet(
      `${this.API_URL_LEAD_CHECKOUT}/product/get/${this.leadId}`
    );
    return response;
  };

  getCheckoutSQLProduct = async (): Promise<ProductI> => {
    const response: ProductI = await this.generalGet(
      `${this.API_URL_LEAD}/dashboard/${this.leadId}`
    );
    return response;
  };

  getCheckoutNote = async () => {
    // Born-native: la nota legal del paso de nota la sirve ms-agl vía gateway
    // (constante IT/AGL). El front solo usa el filename del `url`.
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/checkout-note`
      : `${this.API_URL_LEAD_CHECKOUT}/note/get/${this.leadId}`;
    const response = await this.generalGet(url);
    return response;
  };

  getClientStatus = async (): Promise<{
    status: UserStatusT;
    themeColor: ThemeColorsT | "";
    logo: string | null;
  }> => {
    // Born-native: el lead no tiene `contact_lead` en el monolito, así que su
    // status (que gobierna el enrutamiento vía RecoverUserStatus) lo calcula
    // ms-agl desde las tablas native y lo sirve el gateway con la misma forma.
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/status`
      : `${this.API_URL_LEAD}/status/get/${this.leadId}`;
    const response: {
      status: UserStatusT;
      themeColor: ThemeColorsT | "";
      logo: string | null;
    } = await this.generalGet(url);
    return response;
  };

  getInfoLead = async () => {
    // Born-native: sin `contact_lead`, la info (email/phone/status/booleans/
    // capitals) la calcula ms-agl desde tablas native y la sirve el gateway con
    // el mismo shape parcial que consume el front.
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/info`
      : `${this.API_URL_LEAD}/info/${this.leadId}`;
    const response = await this.generalGet(url);
    return response;
  };

  getConfirmationInfo = async () => {
    const response = await this.generalGet(
      `${this.API_URL}/confirmation/info/${this.leadId}`
    );
    return response;
  };

  getInfoLeadV2 = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/info/v2/${this.leadId}`
    );
    return response;
  };

  getProductQuestion = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/get/product/${this.leadId}`
    );
    return response;
  };

  /**
   * Obtiene el árbol completo de preguntas del producto
   * Devuelve todas las preguntas con sus reglas de visibilidad y respuestas previas
   */
  getQuestionPoduct = async () => {
    const response = await this.generalGet(
      `${this.API_URL}/question/product/${this.leadId}`
    );
    return response;
  };

  /**
   * Guarda una respuesta del árbol de preguntas
   */
  saveQuestionTreeAnswer = async ({ questionId, questionName, value }: {
    questionId: number;
    questionName: string;
    value: string
  }) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/question/tree/save`,
      dataRequest: {
        leadId: this.leadId,
        questionId,
        questionName,
        value,
      },
    });
    return response;
  };

  /**
   * Confirma preguntas y avanza a questions_pending
   */
  confirmQuestionsProduct = async () => {
    const response = await this.generalPost({
      url: `${this.API_URL}/question/confirm/product/${this.leadId}`,
      dataRequest: {},
    });
    return response;
  }

  /**
 * Confirma preguntas y avanza a upsell_pending
 */
  confirmQuestions = async () => {
    const response = await this.generalPost({
      url: `${this.API_URL}/question/confirm/${this.leadId}`,
      dataRequest: {},
    });
    return response;
  }

  /**
   * Fuerza la confirmación de preguntas cuando el usuario rechaza el downgrade
   */
  forceConfirmQuestions = async () => {
    const response = await this.generalPost({
      url: `${this.API_URL}/question/force-confirm/${this.leadId}`,
      dataRequest: {},
    });
    return response;
  }

  /**
   * Valida el estado de las preguntas médicas
   * Devuelve { status: "questions_pending" | "upsell_pending" | ... }
   */
  validateMedicalQuestions = async (): Promise<{ status: UserStatusT }> => {
    const response = await this.generalPost({
      url:`${this.API_URL}/question/validate-medical/${this.leadId}`,
      dataRequest: {},
    });
    return response;
  };

  /**
   * Obtiene el árbol de preguntas médicas
   */
  getQuestionStep = async () => {
    const response = await this.generalGet(
      `${this.API_URL}/question/medical/${this.leadId}`
    );
    return response;
  };

  // Organic native (F5b): árbol médico native (forma ms-agl, sin adaptar). El
  // género lo resuelve el gateway server-side del quote. La vista adapta la
  // respuesta a QuestionTreeResponse con i18n (adaptNativeMedicalTree).
  getMedicalTreeNative = async () => {
    return this.generalGet(
      `${customerApiBaseUrl()}/customer/leads/${this.leadId}/questionnaire`
    );
  };

  // Organic native (F5b): confirma el cuestionario con las respuestas acumuladas
  // (el native no guarda una a una). Devuelve la decisión de UW en el 200.
  confirmQuestionnaireNative = async (answers: Record<string, unknown>) => {
    return this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/questionnaire/confirm`,
      dataRequest: { answers },
    });
  };

  getProfessions = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/get-scor-professions`
    );
    return response;
  };

  getQuestion = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/get/${this.leadId}`
    );
    return response;
  };

  getQuestionField = async (field: string) => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/${field}/${this.leadId}`
    );
    return response;
  };

  getQuestionNomenclator = async (category: string) => {
    if (category) {
      // Organic native: el nomenclator del monolito no aplica (sin branchear,
      // el build native pegaba al host del SPA → devolvía HTML → crash). El
      // gateway sirve country_it (catálogo ISO estático) + city_italia (ms-agl).
      const url = isNativeMode()
        ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/nomenclator/${category}`
        : `${this.API_URL_LEAD}/question/nomenclator/get/${category}`;
      const response = await this.generalGet(url);
      return response;
    }
  };

  getQuoteData = async (leadId: string): Promise<DataPublicI> => {
    // Organic native (F5a): el lead born-native no vive en el monolito y su quote
    // tampoco; `/lead/quote` daría 401 (el monolito no reconoce el lead-token del
    // gateway). El precio es el del `rate` (el mismo que devolvió createLead): se
    // re-tarifica con el `/organic/quote` PÚBLICO (sin auth) desde la oferta organic
    // guardada en sesión y se mapea a DataPublicI. Incluye el rango de capital
    // (grid AGL) para el slider de QuotePage: moverlo parchea la oferta y
    // re-tarifica (QuotePage.controller). Si no hay oferta en sesión (no debería
    // pasar en organic; el cierre born-native no pasa por QuotePage) se cae al
    // monolito.
    const offer = organicNativeEnabled() && isNativeMode() ? readOrganicOffer() : null;
    if (null !== offer) {
      return this.mapOrganicQuoteData(leadId, offer);
    }
    const response = await this.generalGet(
      `${this.API_URL}/lead/quote/${leadId}`
    );
    return response;
  };

  // Sintetiza el DataPublicI de QuotePage para el funnel organic native a partir
  // de la oferta en sesión, re-tarificando con el `/organic/quote` del gateway. El
  // precio (y, cuando ms-agl lo aplique, el descuento) es el MISMO que se cobrará:
  // la fuente de verdad del importe es ms-agl, no el front.
  private mapOrganicQuoteData = async (
    leadId: string,
    offer: { birthDate: string; gender: string; smoker: boolean; capital: number; imc?: { value: number } | null }
  ): Promise<DataPublicI> => {
    // El rango del slider de capital (grid AGL) va en paralelo y es best-effort:
    // sin él la página sale igual, solo que sin slider.
    const [rate, boundaries] = await Promise.all([
      this.generalPost({
        url: `${nativeGatewayBaseUrl()}/organic/quote`,
        dataRequest: {
          birthDate: offer.birthDate,
          gender: offer.gender,
          capital: offer.capital,
          smoker: offer.smoker,
          imc: offer.imc ?? null,
        },
      }),
      this.getCapitals(offer.birthDate).catch(() => null),
    ]);
    const min = Number(boundaries?.minCapital);
    const max = Number(boundaries?.maxCapital);
    const capitals: CapitalsResponse | undefined =
      Number.isFinite(min) && Number.isFinite(max) && max > min
        ? { DEATH: { active: true, available: true, capital: offer.capital, min, max } }
        : undefined;
    const monthly =
      "RATED" === rate?.outcome && "number" === typeof rate?.monthly ? rate.monthly : 0;
    // El descuento permanente IT (10_sconto) lo aplica ms-agl en la prima (fuente
    // del cobro); el front refleja lo que devuelve el rate. Mientras el rate no
    // traiga neto/regular por separado, regular = priceToPay (sin tachado).
    const regular =
      "number" === typeof rate?.regularMonthly && rate.regularMonthly > monthly
        ? rate.regularMonthly
        : monthly;
    return {
      availableTraits: { DEATH: true, DISABILITY: false },
      capital: offer.capital,
      capitals,
      coverage: "DEATH",
      dateOfBirth: offer.birthDate,
      exclusions: [],
      firstName: "",
      hasTelesubscription: false,
      intention: "",
      leadId,
      leadInternalId: 0,
      price: regular,
      priceMonthlyPromotion: monthly,
      priceSummary: {
        hasDiscount: regular > monthly,
        hasProrrate: false,
        regularPrice: regular,
        firstPayment: monthly,
        priceToPay: monthly,
      },
      afterPromoText: "",
      beforePromoText: "",
      themeColor: "" as keyof ThemeColorsT,
      logo: "",
    };
  };

  getRegionalData = async (zipCode: string) => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/regionalData/get/${zipCode}`
    );
    return response;
  };

  getCityFromZipCode = async (zipCode: string) => {
    const response = await this.generalGet(
      `${this.API_URL}/it/cities/${zipCode}/`
    );
    return response;
  };

  getReset = async (email: string) => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/reset/${email}`
    );
    return response;
  };

  getToken = async (leadId: string) => {
    // Organic native (F5a): el token lo acuñó el gateway en /organic/leads y el
    // front lo guardó; aquí se devuelve con la misma forma que el monolito
    // ({token}) para que el getToken→setToken de QuotePage funcione sin ramas.
    // No hay endpoint que re-emita el token por leadId (sería acuñar tokens sin
    // auth); el token vive en la sesión.
    if (isNativeMode()) {
      return { token: readOrganicToken(leadId), brokerId: null, email: null };
    }
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/token/${leadId}`
    );
    return response;
  };

  callMe = async (phone: string | number, leadId?: string) => {
    // Native (Portal Cliente IT): al gateway, con la sesión del lead (quien lo
    // usa crea el repositorio sin token). Avisa a los asesores sin tocar la ficha.
    if (isNativeMode()) {
      return this.generalPost({
        url: `${customerApiBaseUrl()}/customer/leads/${leadId || this.leadId}/callme`,
        dataRequest: { phone },
        header: { Authorization: `Bearer ${readOrganicToken(leadId || this.leadId)}` },
      });
    }
    // Monolito: con sesión del propio lead, además actualiza el teléfono; sin
    // sesión solo audita y avisa. Token solo si el lead enviado es el del
    // repositorio (un token ajeno daría 403).
    const targetLead = leadId || this.leadId;
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/callme`,
      dataRequest: { phone, leadId: targetLead },
      header: this.header && targetLead === this.leadId ? this.header : {},
    });

    return response;
  };

  leadAggregator = async ({ phone, date }: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/aggregator/callmeback`,
      dataRequest: { phone, date },
    });

    return response;
  };

  login = async (userData: any) => {
    // Login del cliente NATIVE (magic-link del gateway): el born-native/organic no
    // vive en el monolito, así que canjea el enlace en el gateway, no en /lead/login.
    // Gate: organicNativeEnabled() (build del canary native — en la pantalla de
    // login de una visita de retorno aún no hay magic-link, así que isNativeMode no
    // basta) o isNativeMode() (ya dentro, al abrir el enlace con ?native=1). Mismo
    // contrato de respuesta {token,id,status,email,themeColor,logo}.
    //
    // Solo el enlace del gateway es un JWT. El OTP de los emails del monolito
    // (10 caracteres, `app.life5.it/login/<otp>`: bienvenida, impago, renovación,
    // SMS, PDF de la oferta…) se canjea en el monolito aunque el build sea native;
    // si no, el gateway lo rechaza con 401. Esa sesión es del monolito: se sale
    // del modo native para que sus llamadas no se anulen.
    const nativeLink = isGatewayLoginLink(userData?.otp);
    if (!nativeLink && (organicNativeEnabled() || isNativeMode())) {
      clearNativeMode();
    }
    if (nativeLink && (organicNativeEnabled() || isNativeMode())) {
      return this.redeemNativeLink(userData?.otp);
    }
    try {
      return await this.generalPost({
        url: `${this.API_URL_LEAD}/login`,
        dataRequest: { ...userData },
        header: {
          "Content-Type": "application/json",
        },
      });
    } catch (error: any) {
      // El alta ya vive en native (migración JIT de ms-agl, 28-oct-2026): el
      // monolito no abre sesión legacy y da un enlace native de 15 min, que se
      // canjea en el gateway como cualquier enlace native (sea cual sea el build).
      if (
        409 === error?.status &&
        "LEAD_SERVED_NATIVE" === error?.message &&
        "string" === typeof error?.nativeLoginToken &&
        "" !== nativeGatewayBaseUrl()
      ) {
        return this.redeemNativeLink(error.nativeLoginToken);
      }
      throw error;
    }
  };

  private redeemNativeLink = async (link: string) => {
    const native = await this.generalPost({
      url: `${nativeGatewayBaseUrl()}/auth/lead/login`,
      dataRequest: { otp: link },
    });
    // Arranca la sesión native: guarda el token (lo lee el getToken native que
    // alimenta a las llamadas /customer/*) y entra en modo native, para que el
    // funnel al que enruta el login corra native y no caiga al monolito. Igual
    // que postLeadOrganic tras crear el lead.
    if (native?.token) {
      storeOrganicToken(native.token);
      enterNativeMode();
    }
    return native;
  };

  loginOtp = async (email: string) => {
    if (organicNativeEnabled() || isNativeMode()) {
      return this.generalGet(
        `${nativeGatewayBaseUrl()}/auth/lead/otp/${encodeURIComponent(email)}`
      );
    }
    const response = await this.generalGet(`${this.API_URL_LEAD}/otp/${email}`);

    return response;
  };

  GoogleSSO = async (code: string) => {
    // Cliente native: el gateway verifica el ID token de Google, resuelve la
    // PERSONA en Identity y devuelve la misma sesión que el magic-link (mismo gate
    // que `login`/`loginOtp`). 404 = sin cuenta, 401 = token inválido o email de
    // varias personas. Los demás mercados siguen en el SSO del monolito.
    if (organicNativeEnabled() || isNativeMode()) {
      try {
        const native = await this.generalPost({
          url: `${nativeGatewayBaseUrl()}/auth/lead/google`,
          dataRequest: { credential: code },
        });
        if (native?.token) {
          storeOrganicToken(native.token);
          enterNativeMode();
        }
        return native;
      } catch (error: any) {
        // 409 LEGACY_ACCOUNT: la persona solo tiene leads del monolito, que el
        // funnel native no sabe servir → sale de native y entra por el SSO legacy.
        if (409 !== error?.status || "LEGACY_ACCOUNT" !== error?.message) {
          throw error;
        }
        clearNativeMode();
      }
    }
    const response = await this.generalGet(
      `${this.API_URL}/public/lead/sso/login?code=${code}`
    );

    return response;
  };

  editQuestion = async (questionId: number | string) => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/edit/${this.leadId}/${questionId}`
    );

    return response;
  };

  editProductQuestion = async (questionId: number | string) => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/question/edit/product/${this.leadId}/${questionId}`
    );

    return response;
  };

  partnerRastreator = async ({ token, data }: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/partner/rastreator/${token}`,
      dataRequest: data,
    });

    return response;
  };

  getPartnerFacile = async (token: string): Promise<FacileLeadI> => {
    // Build native: el token lo descifra el gateway (sin monolito), con el mismo
    // shape. El token va en el cuerpo, no en la URL.
    if (organicNativeEnabled()) {
      return this.generalPost({
        url: `${nativeGatewayBaseUrl()}/organic/partners/facile/token`,
        dataRequest: { token },
      });
    }
    const response = await this.generalGet(
      `${this.API_URL}/public/facile/token/${token}`
    );

    return response;
  };

  getPartnerFacilePrice = async ({
    dateOfBirth,
    gender,
    capital,
    nicotine,
  }: {
    dateOfBirth: string;
    gender: string;
    capital: number;
    nicotine: boolean;
  }): Promise<FacileBasePriceI> => {
    // Build native: el mismo preview que el calculador organic native (con el
    // cupón por defecto del año 1, si lo hay), no la tarifa del monolito.
    if (organicNativeEnabled()) {
      const rate = await this.generalPost({
        url: `${nativeGatewayBaseUrl()}/organic/quote`,
        dataRequest: {
          birthDate: facileBirthDate(dateOfBirth),
          gender: facileGender(gender),
          capital: Number(capital),
          smoker: true === nicotine,
        },
      });
      return facilePricesFromRate(rate);
    }
    const response = await this.generalGet(
      `${this.API_URL}/public/facile/basic/prices/${dateOfBirth}/${capital}/${nicotine}/${gender}`
    );

    return response;
  };

  postOrganicEmail = async () => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/organic/email`,
      dataRequest: {},
    });

    return response;
  };

  postQuestionField = async ({ field, value }: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/question/field/${this.leadId}`,
      dataRequest: { questionField: field, value: String(value) },
    });

    return response;
  };

  postFacileOrganic = async (data: PostFacileLeadI) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/organic/facile`,
      dataRequest: data,
    });

    return response;
  };

  resetQuote = async () => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/reset_quote`,
      dataRequest: {},
    });

    return response;
  };

  saveQuestion = async ({ answer, questionId }: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/question/save`,
      dataRequest: {
        leadId: this.leadId,
        answer: answer,
        questionId: questionId,
      },
    });

    return response;
  };

  saveData = async (data: any) => {
    // Born-native: datos del asegurado → gateway (agl_insured); el BFF normaliza
    // el shape. La respuesta no se usa (el caller solo navega al resolver).
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/insured`
      : `${this.API_URL_LEAD_CHECKOUT}/data/save/${this.leadId}`;
    const response = await this.generalPost({
      url,
      dataRequest: {
        ...data,
      },
    });

    return response;
  };

  getTerzoReferente = async () => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/referrer`
      : `${this.API_URL_LEAD}/contact-person/${this.leadId}`;
    const response = await this.generalGet(url);

    return response;
  };

  saveTerzoReferente = async (data: any) => {
    // Born-native: terzo referente → gateway (agl_referrer), F2c.
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/referrer`
      : `${this.API_URL_LEAD}/contact-person/add`;
    const response = await this.generalPost({
      url,
      dataRequest: {
        ...data,
      },
    });

    return response;
  };

  saveBeneficiaries = async ({ types, beneficiaries }: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD_BENEFICIARIES}/save`,
      dataRequest: {
        leadId: this.leadId,
        type: types,
        beneficiaries: beneficiaries,
      },
    });

    return response;
  };

  saveBeneficiariesTypes = async (beneficiariesTypes: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD_BENEFICIARIES}/save/types`,
      dataRequest: {
        leadId: this.leadId,
        beneficiariesTypes: beneficiariesTypes,
      },
    });

    return response;
  };

  /**
   * Whether the funnel may offer the pledge and exclusive clauses (AXA V10 /
   * AXA_WL V4 contracts) and the clause the lead holds now. Born-native IT never
   * offers them: the monolith endpoint is not called there.
   */
  getBeneficiaryDesignation = async (): Promise<BeneficiaryDesignationViewI> => {
    if (isNativeMode()) {
      return { available: false, designation: null };
    }
    return this.generalGet(`${this.API_URL_LEAD_BENEFICIARIES}/designation/${this.leadId}`);
  };

  /**
   * Replaces the designation with a pledge or exclusive clause. Do not call the
   * save/types or save endpoints afterwards: the «Otros beneficiarios» path drops
   * the clause rows.
   */
  postBeneficiaryDesignation = async (
    designation: BeneficiaryDesignationT
  ): Promise<BeneficiaryDesignationViewI> =>
    this.generalPost({
      url: `${this.API_URL_LEAD_BENEFICIARIES}/designation/${this.leadId}`,
      dataRequest: designation,
    });

  track = async (data: any) => {
    if (isNativeMode()) {
      return this.nativeTrack(data);
    }
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/track/v2`,
      dataRequest: data,
    });

    return response;
  };

  trackCompare = async (data: any) => {
    // Native: la visita de vuelta no publica. El monolito solo guarda el toque si
    // cambió respecto al último y ms-agl no guarda toques (el CRM se queda con el
    // primero): sin esto cada visita sería otro LeadTrackCreated.
    if (isNativeMode()) {
      return null;
    }
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/track/v2/compare`,
      dataRequest: data,
    });

    return response;
  };

  /**
   * Atribución de marketing en native: el `/lead/track/v2` del monolito no
   * recibe nada en native (choke-point), así que va al gateway con la sesión del
   * lead, que la pasa a ms-agl. Sin lead o sin sesión no hay a quién atribuirla.
   * El compare va a la misma ruta: ms-agl solo publica si el toque cambia.
   */
  private nativeTrack = async (data: any) => {
    const lead = data?.leadUlid;
    const token = readOrganicToken(lead);
    if (!lead || "" === token) {
      return null;
    }
    return this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${lead}/track`,
      dataRequest: data,
      header: { Authorization: `Bearer ${token}` },
    });
  };

  saveEvent = async (data: any) => {
    const isLocalhost = window.location.hostname === "localhost";
    if (isLocalhost) return;
    const response = await this.generalPost({
      url: `${this.API_URL_EVENTS}`,
      dataRequest: data,
    });

    return response;
  };

  updateBeneficiariesCapital = async (beneficiaries: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD_BENEFICIARIES}/update/capital`,
      dataRequest: { leadId: this.leadId, beneficiaries: beneficiaries },
    });

    return response;
  };

  updateBeneficiariesNotify = async (data: any) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD_BENEFICIARIES}/update/email`,
      dataRequest: data,
    });

    return response;
  };

  updateEffectDate = async (effectDate: string) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD_CHECKOUT}/lead/updateEffectDate/${this.leadId}/${effectDate}`,
      dataRequest: {},
    });

    return response;
  };

  updatePeriod = async (purchasePeriod: any) => {
    // Organic native: re-materializa el fee_schedule con la nueva periodicidad en
    // el gateway (el mensual AGL no es yearly/12). El monolito no aplica.
    if (isNativeMode()) {
      return this.generalPost({
        url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/period`,
        dataRequest: {
          frequency: String(purchasePeriod).toUpperCase() === "YEARLY" ? "YEARLY" : "MONTHLY",
        },
      });
    }
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/period/update/${this.leadId}`,
      dataRequest: {
        period: purchasePeriod,
      },
    });

    return response;
  };

  calculateMultiTraitPrices = async (
    leadId: string,
    capitalsByTrait: Record<string, number>
  ): Promise<any> => {
    const response = await this.generalPut({
      url: `${this.API_URL}/lead/traits/${leadId}`,
      dataRequest: { capitalsByTrait },
    });
    return response;
  };

  getCapitals = async (birthDate: string) => {
    // Organic native: el rango del slider lo sirve el gateway desde la primera
    // pantalla del calculador (el monolito es otro origen en native.life5.it → CORS).
    // AGL es DEATH-only, sin "extended".
    const url = calculatorOnGateway()
      ? `${nativeGatewayBaseUrl()}/organic/boundaries?birthDate=${encodeURIComponent(birthDate)}`
      : `${this.API_URL_LEAD}/capital/tarifier/boundaries/${birthDate}`;
    return this.generalGet(url);
  };

  getCapitalsExtended = async (birthDate: string) => {
    const url = calculatorOnGateway()
      ? `${nativeGatewayBaseUrl()}/organic/boundaries?birthDate=${encodeURIComponent(birthDate)}`
      : `${this.API_URL_LEAD}/capital/tarifier/pureBoundaries/${birthDate}`;
    return this.generalGet(url);
  };

  postLeadOrganic = async (
    data: OrganicRequestI
  ): Promise<OrganicResponseI> => {
    // Organic native (F5a): se decide por el gate PROPIO organicNativeEnabled()
    // (en el calculador aún NO estamos en isNativeMode: no hubo magic link). El
    // lead ORGANIC nace en el gateway (/organic/leads), que acuña su token y
    // devuelve el precio; recién ENTONCES entramos en native, para que un fallo
    // deje la sesión en monolito (fallback seguro).
    if (organicNativeEnabled()) {
      const native = await this.generalPost({
        url: `${nativeGatewayBaseUrl()}/organic/leads`,
        dataRequest: {
          birthDate: data.birthDate,
          gender: data.gender,
          capital: data.capital,
          smoker: "yes" === data.nicotine,
          email: data.email,
          firstName: data.firstNameRegister || null,
          lastName: data.lastNameRegister || null,
          phone: data.phone || null,
        },
      });
      storeOrganicToken(native.token);
      // Guarda la oferta para el upsell (el imc real se parchea en el médico).
      storeOrganicOffer({
        birthDate: data.birthDate,
        gender: data.gender,
        smoker: "yes" === data.nicotine,
        capital: data.capital,
        imc: null,
      });
      enterNativeMode();
      return this.mapOrganicNative(native, data);
    }

    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/organic`,
      dataRequest: data,
    });

    return response;
  };

  // Mapea la respuesta native de /organic/leads (lead + token + envelope v2 del
  // rate) a la forma OrganicResponseI que consume el calculador. AGL es producto
  // ÚNICO (DEATH, con MALATTIA_TERMINALE embebido en el grid) → basic = premium =
  // cuota mensual; sin telesuscripción ni exclusiones. Si el rate no es RATED
  // (out-of-grid/rejected) el precio cae a 0 y el funnel downstream lo gestiona.
  private mapOrganicNative = (
    native: {
      leadUlid: string;
      isFirstIncoming?: boolean;
      rate?: { outcome?: string; monthly?: number };
    },
    data: OrganicRequestI
  ): OrganicResponseI => {
    const rate = native.rate ?? {};
    const monthly =
      "RATED" === rate.outcome && "number" === typeof rate.monthly ? rate.monthly : 0;
    return {
      leadId: native.leadUlid,
      // Solo el primer lead de ese email cuenta como `newLead` (regla del
      // monolito); sin el campo (gateway anterior) se mantiene true.
      isFirstIncoming: false !== native.isFirstIncoming,
      firstNameRegister: data.firstNameRegister,
      coverage: "DEATH",
      exclusions: [],
      basicFrom: monthly,
      basicTo: monthly,
      premiumFrom: monthly,
      premiumTo: monthly,
      capital: data.capital,
      basicPrice: monthly,
      premiumPrice: monthly,
      promotedFrom: monthly,
      hasTelesubscription: false,
      capitalsByTrait: data.capitalsByTrait,
    };
  };

  assingABTest = async (abValue?: number) => {
    const response = await this.generalPost({
      url: `${this.API_URL_LEAD}/assign-is-ab-test`,
      dataRequest: { leadId: this.leadId, abValue: abValue ?? 1 },
    });

    return response;
  };

  getDocuments = async (): Promise<DocumentsI> => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/documents`
      : `${this.API_URL_DOCUMENT}/get/${this.leadId}`;
    const response: DocumentsI = await this.generalGet(url);

    return response;
  };

  getApprovedPolices = async () => {
    const response = await this.generalGet(
      `${this.API_URL}/info/policies/approved`
    );
    return response;
  };

  getPaymentData = async () => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/payment-data`
      : `${this.API_URL_LEAD}/payment/${this.leadId}`;
    const response = await this.generalGet(url);
    return response;
  };

  getUpsellData = async () => {
    const response = await this.generalGet(
      `${this.API_URL_LEAD}/upsell/${this.leadId}`
    );
    return response;
  };

  // Organic native (F5c): re-prepara el quote con 3 capitales (inicial ±25k) y
  // devuelve el catálogo (las 3 pricings a mostrar). Los inputs actuariales son
  // autodeclarados y los trae la vista de su sesión (idénticos al quote inicial).
  getUpsellOptionsNative = async (input: {
    birthDate?: string;
    gender?: string;
    smoker?: boolean;
    imc?: { value: number } | null;
    capital?: number;
  }) => {
    return this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/upsell`,
      dataRequest: input,
    });
  };

  // Organic native (F5c): el cliente elige un capital → select + materializa el
  // fee_schedule de esa oferta (el gateway lo necesita para que el precio native
  // resuelva en pago/checkout) + lock. Los inputs actuariales (autodeclarados) y
  // el capital ELEGIDO van en el body; puede cambiar hasta el pago.
  selectUpsellNative = async (input: {
    pricingId: string;
    birthDate?: string;
    gender?: string;
    smoker?: boolean;
    imc?: { value: number } | null;
    capital?: number;
    frequency?: "MONTHLY" | "YEARLY";
    effectDate?: string;
  }) => {
    return this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/upsell/select`,
      dataRequest: input,
    });
  };

  getPricingCapitals = async () => {
    const response = await this.generalGet(
      `${this.API_URL}/pricing/capitals/${this.leadId}`
    );
    return response;
  };

  postConfirmRenewalAgl = async (leadId: string, token: string, year: string): Promise<FacileLeadI> => {
    const response = await this.generalPost({
      url: `${this.API_URL}/public/it/renewal/confirm`,
      dataRequest: { leadId: leadId, token: token, year: year },
    });

    return response;
  };

  getLeadDocumentation = async (
    leadId: string
  ): Promise<GetLeadDocumentationI | null> => {
    let documentation: GetLeadDocumentationI | null = null;
    try {
      // Born-native: read-back de biometría desde el gateway de cliente (F1a).
      const url = isNativeMode()
        ? `${customerApiBaseUrl()}/customer/leads/${leadId}/documentation`
        : `${this.API_URL_LEAD}/documentation/${leadId}`;
      const response = await this.generalGet(url);
      documentation = response;
    } catch (error) {
      throw error;
    }
    return documentation;
  };

  postLeadDocumentationUploadImage = async (
    leadId: string,
    data: {
      leadId: string;
      content: string;
      type: string;
    }
  ): Promise<any> => {
    let documentation: any = null;
    try {
      if (isNativeMode()) {
        // Born-native: al gateway de cliente (/customer/.../identification/image).
        // El mimetype sale del prefijo data-URI del contenido; si no, jpeg.
        const mimetype = data.content.match(/^data:([^;]+);/)?.[1] ?? "image/jpeg";
        documentation = await this.generalPost({
          url: `${customerApiBaseUrl()}/customer/leads/${leadId}/identification/image`,
          dataRequest: { imageType: data.type, mimetype, content: data.content },
        });
        return documentation;
      }
      const response = await this.generalPost({
        url: `${this.API_URL_LEAD}/documentation/upload/image/${leadId}`,
        dataRequest: data,
      });
      documentation = response;
    } catch (error) {
      throw error;
    }
    return documentation;
  };

  postLeadDocumentationDocumentationType = async (
    leadId: string,
    documentationType: string
  ): Promise<any> => {
    let documentation: any = null;
    try {
      if (isNativeMode()) {
        documentation = await this.generalPost({
          url: `${customerApiBaseUrl()}/customer/leads/${leadId}/identification`,
          dataRequest: { type: documentationType },
        });
        return documentation;
      }
      const response = await this.generalPost({
        url: `${this.API_URL_LEAD}/documentation/create/${leadId}/${documentationType}`,
        dataRequest: {
          leadId,
          documentationType
        },
      }
      );
      documentation = response;
    } catch (error) {
      throw error;
    }
    return documentation;
  };

  putLeadDocumentationDocumentationType = async (
    leadId: string,
    documentType: string
  ): Promise<any> => {
    let documentation: any = null;
    try {
      const response = await this.generalPut({
        url: `${this.API_URL_LEAD}/documentation/update/${leadId}/${documentType}`,
        dataRequest: {
          leadId: leadId,
          documentType: documentType
        }
      }
      );
      documentation = response;
    } catch (error) {
      throw error;
    }
    return documentation;
  };

  deleteLeadDocumentationDeleteImage = async (
    leadId: string,
    imageType: string
  ): Promise<any> => {
    let documentation: any = null;
    try {
      const response = await this.generalDelete({
        url: `${this.API_URL_LEAD}/documentation/delete/image/${leadId}/${imageType}`,
        dataRequest: { leadId, imageType },
      }
      );
      documentation = response;
    } catch (error) {
      throw error;
    }
    return documentation;
  };

  postLeadDocumentationSave = async (leadId: string): Promise<any> => {
    let documentation: any = null;
    try {
      if (isNativeMode()) {
        documentation = await this.generalPost({
          url: `${customerApiBaseUrl()}/customer/leads/${leadId}/identification/save`,
          dataRequest: {},
        });
        return documentation;
      }
      const response = await this.generalPost({
        url: `${this.API_URL_LEAD}/documentation/save/${leadId}`,
        dataRequest: { leadId }
      });
      documentation = response;
    } catch (error) {
      throw error;
    }
    return documentation;
  };

  // BFF Dashboard endpoints

  /**
   * Get beneficiaries for dashboard (BFF endpoint)
   * Returns empty array for IT market
   */
  getBeneficiaryDashboard = async (): Promise<BeneficiaryI[]> => {
    // Born-native: el dashboard de beneficiarios lo sirve el gateway (F1a); en IT
    // es [] por diseño, igual que el monolito.
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/dashboard/beneficiary`
      : `${this.API_URL}/dashboard/beneficiary/${this.leadId}`;
    const response: BeneficiaryI[] = await this.generalGet(url);
    return response;
  };

  /**
   * Get policy data for dashboard (BFF endpoint)
   * Includes traits with capitals, dates, and policy number
   */
  getPolicyDashboard = async (): Promise<PolicyI> => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/dashboard/policy`
      : `${this.API_URL}/dashboard/policy/${this.leadId}`;
    return this.generalGet(url);
  };

  /**
   * Get pricing data for dashboard (BFF endpoint)
   * Includes promotion, period, fee collection, prices, and showPromo flag
   */
  getPricingDashboard = async (): Promise<PricingI> => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/dashboard/pricing`
      : `${this.API_URL}/dashboard/pricing/${this.leadId}`;
    const response: PricingI = await this.generalGet(url);
    return response;
  };

  /**
   * Portal Cliente como punto de entrada único (2026-10-15): otras cotizaciones
   * de la misma persona, para listarlas en el Dashboard junto a la póliza
   * activa. Solo native (gateway); gated en la llamada por
   * `customerProjectsEnabled()`. Contrato pendiente del lado gateway — ver
   * docs-central agl/contratos/PROPUESTA_customer_self_service_projects_v1_2026-10-15.md.
   */
  getPersonProjects = async (): Promise<PersonProjectI[]> => {
    const url = `${customerApiBaseUrl()}/customer/leads/${this.leadId}/dashboard/projects`;
    const response: PersonProjectI[] = await this.generalGet(url);
    return response;
  };

  /**
   * "Continuar con este presupuesto" sobre OTRA cotización native de la misma
   * persona: el gateway comprueba que es de la misma persona y emite la sesión
   * del lead destino (mismo contrato que el login). Guarda el token native.
   */
  switchToLead = async (targetLeadUlid: string): Promise<SwitchLeadResponseI> => {
    const response: SwitchLeadResponseI = await this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/switch/${targetLeadUlid}`,
      dataRequest: {},
    });
    if (response?.token) {
      storeOrganicToken(response.token);
    }
    return response;
  };

  /**
   * «Archivia preventivo» (30-oct-2026): archiva otra cotización de la misma
   * persona o la de la sesión. El gateway comprueba que es de la misma persona y
   * ms-agl, que no hay nada emitido ni cobrado. Si era la de la sesión y queda otra
   * en curso, el gateway devuelve su sesión: se guarda el token native.
   */
  archiveQuote = async (targetLeadUlid: string): Promise<ArchiveQuoteResponseI> => {
    const response: ArchiveQuoteResponseI = await this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/quotes/${targetLeadUlid}/archive`,
      dataRequest: {},
    });
    if (response?.session?.token) {
      storeOrganicToken(response.session.token);
    }
    return response;
  };

  /**
   * «Modifica capitale» (04-nov-2026), vista previa: si el preventivo de la misma
   * persona admite el cambio, lo vigente y, con `capital`, la prima de ese capital.
   * No graba nada.
   */
  previewCapitalChange = async (
    targetLeadUlid: string,
    capital?: number
  ): Promise<CapitalChangePreviewI> => {
    const query = undefined === capital ? "" : `?capital=${encodeURIComponent(String(capital))}`;
    const response: CapitalChangePreviewI = await this.generalGet(
      `${customerApiBaseUrl()}/customer/leads/${this.leadId}/quotes/${targetLeadUlid}/capital${query}`
    );
    return response;
  };

  /**
   * «Modifica capitale»: graba el capital nuevo del preventivo (oferta elegida y
   * calendario de cuotas). `operationId` hace idempotente el reintento.
   */
  changeCapital = async (
    targetLeadUlid: string,
    capital: number,
    operationId: string
  ): Promise<CapitalChangeResultI> => {
    const response: CapitalChangeResultI = await this.generalPost({
      url: `${customerApiBaseUrl()}/customer/leads/${this.leadId}/quotes/${targetLeadUlid}/capital`,
      dataRequest: { capital, operationId },
    });
    return response;
  };

  /**
   * Submit a withdrawal (recesso) request for the policy. IT-only, allowed only
   * within 30 days of the contract signature (re-validated server-side). This
   * records the request and notifies ops, who finalize the cancellation from
   * the admin — it does NOT cancel the policy directly.
   */
  postWithdrawalRequest = async (): Promise<{ requested: boolean }> => {
    // Native (Portal Cliente IT): ms-agl registra la solicitud y avisa (vía gateway).
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/withdrawal-request`
      : `${this.API_URL}/dashboard/policy/${this.leadId}/withdrawal-request`;
    const response = await this.generalPost({ url, dataRequest: {} });
    return response;
  };

  /**
   * Submit a policy-cancellation request from the customer dashboard. This does
   * NOT cancel the policy; it records the intent and notifies the advisors team
   * by email (with the lead's email and the given reason) so an agent follows
   * up with the customer. The reason is a stable key, not localized text.
   */
  postCancellationRequest = async (
    reason: string
  ): Promise<{ requested: boolean }> => {
    const url = isNativeMode()
      ? `${customerApiBaseUrl()}/customer/leads/${this.leadId}/cancellation-request`
      : `${this.API_URL}/dashboard/policy/${this.leadId}/cancellation-request`;
    const response = await this.generalPost({ url, dataRequest: { reason } });
    return response;
  };

  /**
   * Modification requests of the customer, newest first (signed-request lane:
   * address, name, NIE→DNI, beneficiaries). Feeds the "Modificaciones" history.
   */
  getModificationRequests = async (): Promise<{
    items: {
      id: string;
      type: string;
      typeLabel: string;
      status: string;
      createdAt: string;
      signedAt: string | null;
      completedAt: string | null;
    }[];
  }> => {
    const response = await this.generalGet(
      `${this.API_URL}/dashboard/policy/${this.leadId}/modification-requests`
    );
    return response;
  };

  /**
   * Create a policy modification request (signed lane). The backend creates it
   * as DRAFT and asynchronously sends the PDF to Viafirma; the customer
   * receives the signature link by email. 409 = an active request of the same
   * type already exists.
   */
  postModificationRequest = async (
    type: string,
    payload: Record<string, unknown>
  ): Promise<{ id: string; type: string; status: string }> => {
    const response = await this.generalPost({
      url: `${this.API_URL}/dashboard/policy/${this.leadId}/modification-request`,
      dataRequest: { type, payload },
    });
    return response;
  };

  /**
   * Direct email change (no signature). 409 = the email belongs to another
   * account → the UI must show the "contacta con asesores" notice. The email
   * is also the login credential (loginEmailChanged in the response).
   */
  patchContactEmail = async (
    newEmail: string
  ): Promise<{ changed: boolean; loginEmailChanged: boolean }> => {
    const response = await this.generalPatch({
      url: `${this.API_URL}/dashboard/policy/${this.leadId}/email`,
      dataRequest: { newEmail },
    });
    return response;
  };

  /**
   * Direct phone change (no signature). The code is the one the server sent by
   * SMS to the new phone (sendPhoneChangeCode); the server checks it.
   */
  patchContactPhone = async (
    newPhone: string,
    code: string
  ): Promise<{ changed: boolean }> => {
    const response = await this.generalPatch({
      url: `${this.API_URL}/dashboard/policy/${this.leadId}/phone`,
      dataRequest: { newPhone, code },
    });
    return response;
  };

  /** Asks the server to SMS a phone-change code to the new phone. */
  sendPhoneChangeCode = async (newPhone: string): Promise<{ sent: boolean }> => {
    const response = await this.generalPost({
      url: `${this.API_URL}/dashboard/policy/${this.leadId}/phone/code`,
      dataRequest: { newPhone },
    });
    return response;
  };

  /**
   * "En caso de duda, contactar a asesores" CTA of the beneficiaries screen:
   * emails the advisors team with the lead context so an agent follows up.
   */
  postBeneficiariesAdvice = async (): Promise<{ requested: boolean }> => {
    const response = await this.generalPost({
      url: `${this.API_URL}/dashboard/policy/${this.leadId}/beneficiaries-advice`,
      dataRequest: {},
    });
    return response;
  };

  /**
   * Capital reduction eligibility + optional renewal-premium quote. Without
   * newCapital: eligibility (product version gating), current capital and the
   * renewal/effective dates (10-day rule). With newCapital: adds the renewal
   * premium for the current vs the reduced capital.
   */
  getCapitalReductionContext = async (
    newCapital?: number,
    newCapitalsByTrait?: Record<string, number>
  ): Promise<{
    eligible: boolean;
    currentCapital: number | null;
    minCapital: number;
    // gf-monorepo #10195: reglas de importe de una bajada nueva. Ausentes si el
    // backend aún no las expone (el flujo usa 5.000 y 10.000 por defecto).
    capitalStep?: number;
    minTraitCapital?: number;
    capitalsByTrait: Record<string, number> | null;
    renewalDate: string | null;
    effectiveDate: string | null;
    currentAnnualPremium: string | null;
    newAnnualPremium: string | null;
  }> => {
    const suffix = newCapitalsByTrait
      ? `?capitals=${encodeURIComponent(JSON.stringify(newCapitalsByTrait))}`
      : newCapital !== undefined
        ? `?newCapital=${newCapital}`
        : "";
    const response = await this.generalGet(
      `${this.API_URL}/dashboard/policy/${this.leadId}/capital-reduction${suffix}`
    );
    return response;
  };

}
